import {Alert, AlertTitle, Button, Chip, Stack, Typography} from "@mui/material";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	ConfirmButton,
	Now,
	Tabs,
	TemporalFormat,
	TemporalRangeFormat,
	tryParseInstant,
} from "@variocube/app-ui";
import React, {Fragment} from "react";
import {Link} from "react-router-dom";
import {useBookingApi, useCompartmentApi} from "../../api";
import {DisabledChip} from "../../controls/DisabledChip";
import {useLocationPermission, useTenantId, useTenantPermission} from "../../tenant";
import {queryKeys} from "../../utils/queryKeys";
import {useSearchParam} from "../../utils/search";
import {useNumericParam} from "../../utils/useParam";
import {LocationLinkChip} from "../location/LocationChip";
import {View, ViewHeader} from "../View";
import {CompartmentAuditTimeline} from "./CompartmentAuditTimeline";
import {CompartmentBookingList} from "./CompartmentBookingList";
import {CompartmentStatusChip} from "./CompartmentStatusChip";
import {LockChip} from "./LockChip";
import {LockStatusChip} from "./LockStatusChip";

export function CompartmentView() {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const tenantId = useTenantId();
	const compartmentId = useNumericParam("compartmentId");
	const [tab, setTab] = useSearchParam("tab", "bookings");

	const {getCompartment, getActiveCompartmentBooking} = useCompartmentApi();
	const {updateBookingUntil} = useBookingApi();
	const queryClient = useQueryClient();

	const {isLoading, error, data: compartment} = useQuery(
		queryKeys.compartment(tenantId, compartmentId),
		() => getCompartment(compartmentId),
	);
	const canEdit = useLocationPermission("COMPARTMENT_EDIT", compartment?.location.id);
	const canCreateBooking = useLocationPermission("BOOKING_CREATE", compartment?.location.id);
	const canCreateOwnBooking = useLocationPermission("BOOKING_CREATE_OWN", compartment?.location.id);
	const canEditBooking = useLocationPermission("BOOKING_EDIT", compartment?.location.id);
	const canReadBookings = useTenantPermission("BOOKING_READ");
	const canReadAudit = useTenantPermission("AUDIT_LOG_READ");

	const {data: activeBooking} = useQuery(
		queryKeys.activeCompartmentBooking(tenantId, compartmentId),
		() => getActiveCompartmentBooking(compartmentId),
		{enabled: !!compartment && canEditBooking},
	);

	const endBookingMutation = useMutation({
		mutationFn: (bookingId: number) => updateBookingUntil(bookingId, Now.instant().toString()),
		onSuccess: () => {
			queryClient.invalidateQueries(queryKeys.compartment(tenantId, compartmentId));
			queryClient.invalidateQueries(queryKeys.activeCompartmentBooking(tenantId, compartmentId));
			queryClient.invalidateQueries({queryKey: ["compartmentBookings", tenantId, compartmentId], exact: false});
		},
	});

	const activeBookingFrom = tryParseInstant(activeBooking?.from);
	const activeBookingUntil = tryParseInstant(activeBooking?.until);

	const tabItems = [
		...canReadBookings ? [{label: t(`bookings.title.${wordingFlavor}`), value: "bookings"}] : [],
		...canReadAudit ? [{label: t("audit.title"), value: "audit"}] : [],
	];

	return (
		<View loading={isLoading} error={error}>
			<Breadcrumbs>
				<BreadcrumbLink component={Link} to={".."}>{t(`compartments.title.${wordingFlavor}`)}</BreadcrumbLink>
				<BreadcrumbItem>{compartment?.number}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader
				title={t(`compartments.view.title.${wordingFlavor}`, compartment)}
				subTitle={
					<Stack direction="row" spacing={2}>
						{compartment && <LocationLinkChip location={compartment.location} />}
						{compartment?.size && <Chip label={compartment.size} />}
						{compartment?.lock?.id && <LockChip lock={compartment.lock.id} />}
					</Stack>
				}
				titleAdornment={
					<Stack direction="row" spacing={2}>
						{compartment?.lock?.status && <LockStatusChip status={compartment.lock.status} />}
						{compartment && !compartment.enabled && <DisabledChip />}
						{compartment && <CompartmentStatusChip booked={compartment.booked} />}
					</Stack>
				}
				actions={
					<Stack direction="row" spacing={2}>
						{activeBooking && canEditBooking && (
							<ConfirmButton
								variant="outlined"
								cancel={t("cancel")}
								title={t(`bookings.end.title.${wordingFlavor}`)}
								onConfirm={() => endBookingMutation.mutateAsync(activeBooking.id)}
							>
								<Stack spacing={1}>
									<Typography>{t(`bookings.end.message.${wordingFlavor}`)}</Typography>
									<Typography variant="body2">
										{t(`bookings.view.title.${wordingFlavor}`, activeBooking)}
									</Typography>
									<Typography variant="body2">
										{activeBookingFrom && activeBookingUntil && (
											<TemporalRangeFormat from={activeBookingFrom} until={activeBookingUntil} />
										)}
										{activeBookingFrom && !activeBookingUntil && (
											<TemporalFormat value={activeBookingFrom} />
										)}
									</Typography>
									{activeBooking.users.length > 0 && (
										<Typography variant="body2">
											{activeBooking.users.map(bu => bu.user.displayName).join(", ")}
										</Typography>
									)}
								</Stack>
							</ConfirmButton>
						)}
						{(canCreateBooking || canCreateOwnBooking) && (
							<Button
								variant="contained"
								color="primary"
								disabled={!compartment || !compartment.bookable}
								component={Link}
								to={`/${tenantId}/bookings/new?compartment=${compartment?.id}`}
							>
								{t(`bookings.create.title.${wordingFlavor}`)}
							</Button>
						)}
						{canEdit && (
							<Button variant="outlined" color="primary" component={Link} to="edit">
								{t("edit")}
							</Button>
						)}
					</Stack>
				}
			/>
			{compartment?.lock?.keyOutOfSync && (
				<Alert severity="warning">
					<AlertTitle>{t("compartments.lock.keyOutOfSync.title")}</AlertTitle>
					{t("compartments.lock.keyOutOfSync.message")}
				</Alert>
			)}
			{tabItems.length > 0 && (
				<Fragment>
					<Tabs
						value={tab}
						onChange={(_, tab) => setTab(tab)}
						items={tabItems}
					/>
					{canReadBookings && tab == "bookings" && <CompartmentBookingList />}
					{canReadAudit && tab == "audit" && <CompartmentAuditTimeline />}
				</Fragment>
			)}
		</View>
	);
}
